import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatEur } from '@/lib/writer/telemetryCost';
import { costEur } from '@/lib/writer/telemetryCost';
import {
  turnCostEur,
  type ProjectRow,
  type RecentTurnRow,
  type UserRow,
} from '@/lib/writer/telemetryTypes';

interface Props {
  users: UserRow[];
  projects: ProjectRow[];
  recentTurns: RecentTurnRow[];
  /** Model used for the per-user cost estimate when only totals are known. */
  dominantModel: string;
}

const rate = (a: number, b: number) => (b > 0 ? `${((a / b) * 100).toFixed(0)}%` : '—');

const BreakdownTables: React.FC<Props> = ({ users, projects, recentTurns, dominantModel }) => {
  const [expanded, setExpanded] = React.useState<string | null>(null);

  const userCost = (u: UserRow) =>
    costEur(dominantModel, {
      tokensIn: u.tokens_in,
      tokensCached: u.tokens_cached,
      tokensThinking: u.tokens_thinking,
      tokensOut: u.tokens_out,
      tokensTotal: u.tokens_total,
    });

  return (
    <section className="space-y-4">
      <h2 className="text-lg font-semibold">Breakdowns</h2>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Per user</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground">No activity in this range.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground text-xs">
                <tr>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Turns</th>
                  <th className="py-2 pr-4">Tokens</th>
                  <th className="py-2 pr-4">Cost</th>
                  <th className="py-2 pr-4">Compiles</th>
                  <th className="py-2 pr-4">Success</th>
                  <th className="py-2 pr-4">Last active</th>
                </tr>
              </thead>
              <tbody>
                {[...users]
                  .sort((a, b) => b.turns - a.turns)
                  .map((u) => (
                    <tr key={u.user_id} className="border-t border-border/40">
                      <td className="py-2 pr-4">{u.user_name ?? u.user_id.slice(0, 8)}</td>
                      <td className="py-2 pr-4 tabular-nums">{u.turns}</td>
                      <td className="py-2 pr-4 tabular-nums">{u.tokens_total.toLocaleString()}</td>
                      <td className="py-2 pr-4 tabular-nums">{formatEur(userCost(u))}</td>
                      <td className="py-2 pr-4 tabular-nums">{u.compiles}</td>
                      <td className="py-2 pr-4 tabular-nums">{rate(u.compiles_success, u.compiles)}</td>
                      <td className="py-2 pr-4">{new Date(u.last_active).toLocaleString()}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Per project</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {projects.length === 0 ? (
            <p className="text-sm text-muted-foreground">No project activity in this range.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground text-xs">
                <tr>
                  <th className="py-2 pr-4">Project</th>
                  <th className="py-2 pr-4">Turns</th>
                  <th className="py-2 pr-4">Tokens</th>
                  <th className="py-2 pr-4">Tool calls</th>
                  <th className="py-2 pr-4">Compiles</th>
                  <th className="py-2 pr-4">Success</th>
                  <th className="py-2 pr-4">Last active</th>
                </tr>
              </thead>
              <tbody>
                {[...projects]
                  .sort((a, b) => b.turns - a.turns)
                  .map((p) => (
                    <tr key={p.project_id} className="border-t border-border/40">
                      <td className="py-2 pr-4">
                        <a className="hover:underline" href={`/writer/${p.project_id}`}>
                          {p.project_title ?? p.project_id.slice(0, 8)}
                        </a>
                      </td>
                      <td className="py-2 pr-4 tabular-nums">{p.turns}</td>
                      <td className="py-2 pr-4 tabular-nums">{p.tokens_total.toLocaleString()}</td>
                      <td className="py-2 pr-4 tabular-nums">{p.tool_calls}</td>
                      <td className="py-2 pr-4 tabular-nums">{p.compiles}</td>
                      <td className="py-2 pr-4 tabular-nums">{rate(p.compiles_success, p.compiles)}</td>
                      <td className="py-2 pr-4">{new Date(p.last_active).toLocaleString()}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent turns</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {recentTurns.length === 0 ? (
            <p className="text-sm text-muted-foreground">No turns in this range.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Project</th>
                  <th className="py-2 pr-4">Iter</th>
                  <th className="py-2 pr-4">Tools</th>
                  <th className="py-2 pr-4">In / think / out</th>
                  <th className="py-2 pr-4">Cost</th>
                  <th className="py-2 pr-4">Dur</th>
                  <th className="py-2 pr-4">Model</th>
                </tr>
              </thead>
              <tbody>
                {recentTurns.map((r) => (
                  <React.Fragment key={r.id}>
                    <tr
                      className="border-t border-border/40 cursor-pointer hover:bg-muted/40"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}
                    >
                      <td className="py-2 pr-4">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="py-2 pr-4">{r.owner_name ?? r.owner_id.slice(0, 8)}</td>
                      <td className="py-2 pr-4 max-w-[16ch] truncate">{r.project_title ?? '—'}</td>
                      <td className="py-2 pr-4 tabular-nums">{r.iterations}</td>
                      <td className="py-2 pr-4 tabular-nums">{r.tool_calls}</td>
                      <td className="py-2 pr-4 tabular-nums">
                        {r.tokens_in.toLocaleString()} / {r.tokens_thinking.toLocaleString()} /{' '}
                        {r.tokens_out.toLocaleString()}
                      </td>
                      <td className="py-2 pr-4 tabular-nums">{formatEur(turnCostEur(r))}</td>
                      <td className="py-2 pr-4 tabular-nums">{(r.duration_ms / 1000).toFixed(1)}s</td>
                      <td className="py-2 pr-4 font-mono">{r.model}</td>
                    </tr>
                    {expanded === r.id && (
                      <tr className="bg-muted/30">
                        <td colSpan={9} className="p-3">
                          <div className="font-mono text-[11px] whitespace-pre-wrap">
                            tools: {r.tool_names.length ? r.tool_names.join(', ') : '—'}
                            {'\n'}cached input: {r.tokens_cached.toLocaleString()} · total:{' '}
                            {r.tokens_total.toLocaleString()}
                            {'\n'}thread project: {r.project_id ?? '—'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default BreakdownTables;
